import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, query, getDocs } from 'firebase/firestore';
import { getFirestoreInstance } from '../utils/firebaseHelper';
import UserContext from '../context/UserContext';
import TourContext from '../context/TourContext';

/**
 * This wrapper handles event and date selection for any route that needs it
 * Works with both tours and shows
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Child components to render
 * @param {string} [props.eventType] Optional event type override ('tour' or 'show')
 */
const EventDateWrapper = ({ children, eventType: explicitEventType }) => {
  const { eventId, dateId } = useParams();
  const { userData, isLoading: isUserLoading } = useContext(UserContext);
  const { 
    events, 
    selectedEvent, 
    setSelectedEvent, 
    setSelectedDate, 
    fetchEventsData, 
    updateEventData 
  } = useContext(TourContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // Determine event type from URL path or explicit prop
  const eventType = explicitEventType || location.pathname.split('/')[1];

  // Check if user has access to this event/date
  const checkUserAccess = async (userInfo, eventData, dateDocId) => {
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';

      // Check in assignedUsers subcollection first (modern approach)
      const assignedUserRef = doc(db, collectionName, eventData.id, 'assignedUsers', userInfo.userId);
      const assignedUserDoc = await getDoc(assignedUserRef);
      
      if (assignedUserDoc.exists()) {
        const assignedUserData = assignedUserDoc.data();
        const assignedDates = assignedUserData.assignedDates || [];
        
        // Check if this date is assigned to the user
        if (assignedDates.some(date => date.dateDocId === dateDocId || date.tourDateDocId === dateDocId)) {
          return true;
        }
      }
      
  
      
      // If we reach here, user doesn't have access
      return false;
    } catch (error) {
      console.error('Error checking user access:', error);
      return false;
    }
  };

  // Fetch event data directly from Firestore
  const fetchEventDirectly = async (eventId, dateId) => {
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Fetch the event document
      const eventRef = doc(db, collectionName, eventId);
      const eventDoc = await getDoc(eventRef);
      
      if (!eventDoc.exists()) {
        console.error('Event not found in Firestore');
        setErrorMessage('Event not found');
        return null;
      }
      
      // Get event data
      const eventData = {
        id: eventDoc.id,
        ...eventDoc.data(),
        type: eventType
      };
      
      // Check if user has access to this event/date
      const hasAccess = await checkUserAccess(userData, eventData, dateId);
      
      if (!hasAccess) {
        console.error('User not authorized for this event/date');
        setErrorMessage('You do not have access to this event/date');
        return null;
      }
      
      // If user has access, fetch all dates
      const datesQuery = query(collection(db, collectionName, eventId, 'eventDates'));
      const datesSnapshot = await getDocs(datesQuery);
      
      if (datesSnapshot.empty) {
        console.error('No dates found for this event');
        setErrorMessage('No dates found for this event');
        return null;
      }
      
      // Add dates to event data
      eventData.eventDates = datesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Also make sure the date exists
      const dateExists = eventData.eventDates.some(date => date.id === dateId);
      
      if (!dateExists) {
        console.error('Date not found for this event');
        setErrorMessage('Date not found for this event');
        return null;
      }
      
      return eventData;
    } catch (error) {
      console.error('Error fetching event directly:', error);
      setErrorMessage('Error loading event data');
      return null;
    }
  };

  useEffect(() => {
    const initializeEventData = async () => {
      console.log(`Initializing event data for ${eventType}/${eventId}/${dateId}`);
      
      // Wait for user context to finish loading
      if (isUserLoading) {
        return;
      }

      // Now we can safely check if user is authenticated
      if (!userData?.userId) {
        navigate('/login');
        return;
      }

      // Reset error message
      setErrorMessage('');

      if (eventId && dateId) {
        // First try the normal flow - check if event exists in context
        const event = events.find(e => e.id === eventId && e.type === eventType);
        
        if (event) {
          console.log(`Found event in context: ${event.id} (${event.type})`);
          await setSelectedEvent(event);
          setSelectedDate(dateId);
        } else {
          console.log(`Event not found in local state, trying direct fetch: ${eventId} (${eventType})`);
          
          // Try to fetch the event directly
          try {
            // First try the context's update method
            await updateEventData(eventId, eventType);
            
            // Check if updateEventData successfully loaded the event
            const updatedEvent = events.find(e => e.id === eventId && e.type === eventType);
            
            if (updatedEvent) {
              await setSelectedEvent(updatedEvent);
              setSelectedDate(dateId);
            } else {
              // If not, try a direct Firestore fetch with access check
              console.log('Context update failed, trying direct Firestore fetch');
              const directEvent = await fetchEventDirectly(eventId, dateId);
              
              if (directEvent) {
                await setSelectedEvent(directEvent);
                setSelectedDate(dateId);
                
                // Also refresh events list to include this event
                await fetchEventsData(userData, 1);
              } else {
                // User doesn't have access or event doesn't exist
                console.warn(`Event access denied or not found: ${eventId}`);
                navigate(`/${userData.userName}`);
              }
            }
          } catch (error) {
            console.error(`Error fetching event ${eventId}:`, error);
            setErrorMessage('Error loading event data');
            navigate(`/${userData.userName}`);
          }
        }
      }

      setIsLoading(false);
    };

    initializeEventData();
  }, [userData, isUserLoading, eventId, dateId, eventType, events, fetchEventsData, updateEventData, setSelectedEvent, setSelectedDate, navigate, location.pathname]);

  // Show loading while user context is loading
  if (isUserLoading || isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading {eventType}...</p>
      </div>
    );
  }

  // Show error if any
  if (errorMessage) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{errorMessage}</p>
        <button onClick={() => navigate(`/${userData.userName}`)}>
          Go to Profile
        </button>
      </div>
    );
  }

  // Render children once event and date are loaded
  return children;
};

export default EventDateWrapper;